<template>
    <div class="chat-distribution-by-user-chart" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <DoubleBarChart
                :title="this.$t('report.chatDistByUsers')"
                :xAxisData="getXAxisData()"
                :yAxisData="getYAxisData()"
                :series="getSeries()"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="this.$t('report.mostChatRecipients')" :value="getStats().MaxAgent" />
            <StatCard :title="this.$t('report.leastChatReceiver')" :value="getStats().MinAgent" />
            <StatCard
                :title="this.$t('report.averageChat')"
                :value="formatStat(getStats().AvgAgent).value"
                :unit="formatStat(getStats().AvgAgent).unit"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { mapState } from "vuex";
import fscreen from "fscreen";

const log = log2("ChatDistributionsByUser");
export default Vue.extend({
    name: "ChatDistributionsByUser",
    components: { DoubleBarChart, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.sessionCount")],
            stats: {
                MaxAgent: 0,
                MinAgent: 0,
                AvgAgent: 0,
            },
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: "Chat",
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".chat-distribution-by-user-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("report.user"),
                    dataIndex: "user",
                    key: "user",
                },
                {
                    title: this.$t("message.chatCount"),
                    dataIndex: "chatCount",
                    key: "chatCount",
                },
            ];
        },
        getRows() {
            let items = this.data || [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });

            log("getRows.items", items);
            const rows = items.map((item) => {
                return {
                    user: item.user?.name || "Unknown User",
                    chatCount: item.totalChatCount,
                };
            });
            return rows;
        },
        getStats() {
            let items = this.data || [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });
            const MaxAgent =
                _.chain(items).maxBy("totalChatCount").value()?.user?.name || this.$t("report.noEnoughtData");
            const MinAgent =
                _.chain(items).minBy("totalChatCount").value()?.user?.name || this.$t("report.noEnoughtData");
            const AvgAgent = Math.round(_.meanBy(items, "totalChatCount"));

            return {
                MaxAgent,
                MinAgent,
                AvgAgent,
            };
        },
        getYAxisData() {
            const items = this.data || [];
            return {
                type: "category",

                data: items.map((user) => {
                    const userObj = this.users.find((u) => u._id === user._id);
                    return userObj ? userObj.name : "Unknown User";
                }),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                },
                splitLine: {
                    show: false,
                },
            };
        },
        getXAxisData() {
            const items = this.data || [];
            const max = _.max(items.map((x) => x.totalChatCount || 0)) || 0;
            console.log("getXAxisData", {
                max,
                items,
            });

            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    rotate: -30,
                },
            };
        },
        getSeries() {
            const items = this.data || [];
            log("getSeries.items", items);
            const avg = _.mean(items.map((x) => x.totalChatCount || 0));

            const series = [
                {
                    colorBy: "data",
                    showBackground: true,
                    type: "bar",
                    label: {
                        position: "right",
                        show: true,
                    },
                    data: items.map((item) => item.totalChatCount),
                },
            ];

            return series;
        },
        exportToExcel() {
            let items = this.data || [];
            items = items.map((x) => {
                x.user = this.users.find((u) => u._id === x._id);
                return x;
            });
            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    User: item.user?.name || "Unknown User",
                    "Total Chat Count": item.totalChatCount,
                };
            });
            expertToExcel(excelData, `chat_session_count_by_users.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".chat-distribution-by-user-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.chat-distribution-by-user-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.chat-distribution-by-user-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
